import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Send, Loader2, Paperclip, X, Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { UploadedDocument } from '@/types';

interface Props {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
  isBusy: boolean;
  attachedDocument: UploadedDocument | null;
  onAttach: (file: File) => void;
  onDetach: () => void;
  onToggleAdvanced: () => void;
  placeholder?: string;
  variant?: 'empty' | 'inline';
  mentionedSlideIndex?: number | null;
  onRemoveMention?: () => void;
}

export const ChatComposer: React.FC<Props> = ({
  value,
  onChange,
  onSubmit,
  isBusy,
  attachedDocument,
  onAttach,
  onDetach,
  onToggleAdvanced,
  placeholder,
  variant = 'empty',
  mentionedSlideIndex = null,
  onRemoveMention,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Onboarding tour can request the + menu to open so its "Opções avançadas"
  // step has something to highlight. OnboardingContext dispatches this custom
  // event right before showing a step whose `beforeShowAction` matches.
  useEffect(() => {
    const onTourAction = (evt: Event) => {
      const detail = (evt as CustomEvent<{ action?: string }>).detail;
      if (detail?.action === 'open-chat-plus-menu') {
        setMenuOpen(true);
      }
    };
    window.addEventListener('cai:tour-action', onTourAction);
    return () => window.removeEventListener('cai:tour-action', onTourAction);
  }, []);
  const hasMention = mentionedSlideIndex !== null && mentionedSlideIndex !== undefined;
  // When a slide is mentioned, require free-text instructions — attachment alone
  // is not enough context for a targeted refinement.
  const canSend = !isBusy && (
    hasMention
      ? value.trim().length > 0
      : value.trim().length > 0 || !!attachedDocument
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) onSubmit();
    }
  };

  return (
    <div
      data-tour={variant === 'empty' ? 'chat-input' : undefined}
      className={cn(
        'rounded-2xl border border-border bg-card/40 backdrop-blur p-3 shadow-sm',
        variant === 'empty' ? 'max-w-2xl w-full mx-auto' : 'w-full',
      )}
    >
      {hasMention && (
        <div className="mb-2 flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1.5 bg-primary/15 text-primary border border-primary/30 rounded-full px-2 py-0.5">
            Slide {mentionedSlideIndex! + 1}
            <button
              type="button"
              onClick={onRemoveMention}
              className="hover:text-primary/80"
              aria-label="Remover menção ao slide"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
          <span className="text-muted-foreground">
            Diga o que mudar neste slide.
          </span>
        </div>
      )}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={
          hasMention
            ? 'Descreva a alteração para este slide…'
            : placeholder ?? 'Descreva seu carrossel, cole um link ou anexe um documento…'
        }
        rows={variant === 'empty' ? 3 : 2}
        className="w-full bg-transparent text-base md:text-sm outline-none resize-none placeholder:text-muted-foreground"
        disabled={isBusy}
      />

      {attachedDocument && (
        <div className="mt-2 flex items-center gap-2 text-xs bg-muted/40 rounded-md px-2 py-1.5">
          <Paperclip className="h-3.5 w-3.5" />
          <span className="flex-1 truncate">{attachedDocument.name}</span>
          <button
            type="button"
            onClick={onDetach}
            className="text-muted-foreground hover:text-destructive"
            aria-label="Remover anexo"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 relative">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setMenuOpen((x) => !x)}
            aria-label="Mais ações"
            data-tour="chat-plus"
          >
            <Plus className="h-4 w-4" />
          </Button>
          {menuOpen && (
            <div
              data-tour="chat-plus-menu"
              className="absolute bottom-10 left-0 bg-popover border border-border rounded-md shadow-md p-1.5 text-sm z-10 min-w-[220px]"
            >
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onToggleAdvanced();
                }}
                className="w-full text-left px-3 py-2 hover:bg-muted rounded-sm flex items-center gap-2.5"
              >
                <Settings2 className="h-4 w-4 text-muted-foreground" />
                <span>Opções avançadas</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  fileInputRef.current?.click();
                }}
                className="w-full text-left px-3 py-2 hover:bg-muted rounded-sm flex items-center gap-2.5"
              >
                <Paperclip className="h-4 w-4 text-muted-foreground" />
                <span>Anexar documento</span>
              </button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.md,.jpg,.jpeg,.png,.heic"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onAttach(file);
              e.target.value = '';
            }}
          />
        </div>
        <Button
          type="button"
          disabled={!canSend}
          onClick={onSubmit}
          size="sm"
          className="gap-1"
        >
          {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Enviar
        </Button>
      </div>
    </div>
  );
};
